<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useSiteStore } from '../stores/site'

const canvas = ref<HTMLCanvasElement>()
const site = useSiteStore()
let frame = 0
let rebuildParticles: (() => void) | undefined

function rgb(hex: string | undefined, fallback: string) {
  const value = (hex || fallback).replace('#', '')
  return /^[0-9a-fA-F]{6}$/.test(value)
    ? `${parseInt(value.slice(0, 2), 16)},${parseInt(value.slice(2, 4), 16)},${parseInt(value.slice(4, 6), 16)}`
    : fallback
}

watch(() => [site.theme.primaryColor, site.theme.secondaryColor, site.theme.appearance], () => rebuildParticles?.())

onMounted(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || !canvas.value) return
  const element = canvas.value
  const context = element.getContext('2d')
  if (!context) return
  let width = 0
  let height = 0
  const particles: { x: number; y: number; vx: number; vy: number; radius: number; color: string }[] = []

  const resize = () => {
    const primary = rgb(site.theme.primaryColor, '239,68,68')
    const secondary = rgb(site.theme.secondaryColor, '139,92,246')
    width = element.width = innerWidth
    height = element.height = innerHeight
    particles.length = 0
    const particleCount = Math.min(80, Math.floor(width / 20))
    element.dataset.particleCount = String(particleCount)
    for (let index = 0; index < particleCount; index += 1) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 1.5 + 0.5,
        color: Math.random() > 0.5 ? primary : secondary,
      })
    }
  }

  const draw = () => {
    const light = site.theme.appearance === 'light'
    const particleAlpha = light ? 0.45 : 0.6
    const connectionAlpha = light ? 0.1 : 0.15
    context.clearRect(0, 0, width, height)
    for (let index = 0; index < particles.length; index += 1) {
      const particle = particles[index]
      particle.x += particle.vx
      particle.y += particle.vy
      if (particle.x < 0 || particle.x > width) particle.vx *= -1
      if (particle.y < 0 || particle.y > height) particle.vy *= -1
      context.fillStyle = `rgba(${particle.color},${particleAlpha})`
      context.beginPath()
      context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)
      context.fill()
      for (let nextIndex = index + 1; nextIndex < particles.length; nextIndex += 1) {
        const next = particles[nextIndex]
        const distance = Math.hypot(particle.x - next.x, particle.y - next.y)
        if (distance < 120) {
          context.strokeStyle = `rgba(${particle.color},${connectionAlpha * (1 - distance / 120)})`
          context.lineWidth = 0.5
          context.beginPath()
          context.moveTo(particle.x, particle.y)
          context.lineTo(next.x, next.y)
          context.stroke()
        }
      }
    }
    frame = requestAnimationFrame(draw)
  }

  const visibility = () => {
    if (document.hidden) cancelAnimationFrame(frame)
    else draw()
  }

  resize()
  rebuildParticles = resize
  draw()
  addEventListener('resize', resize)
  document.addEventListener('visibilitychange', visibility)
  onBeforeUnmount(() => {
    cancelAnimationFrame(frame)
    removeEventListener('resize', resize)
    document.removeEventListener('visibilitychange', visibility)
    rebuildParticles = undefined
  })
})
</script>

<template><canvas ref="canvas" class="particle-canvas" aria-hidden="true" /></template>
